import Typography from '@mui/material/Typography'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import type { KNDataTableColumn } from 'components/KNDataTable/types'
import { useRejectedMessagesView } from 'context/RejectedMessagesViewContext'
import { localDate } from 'global/helpers/dates'
import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { RejectedMessage } from './RejectedMessages.types'

interface RejectedMessagesTableProps {
  readonly rejectedMessages: RejectedMessage[]
  readonly pageCount?: number
}

const RejectedMessagesTable = ({
  rejectedMessages,
  pageCount,
}: RejectedMessagesTableProps): ReactElement => {
  const { t } = useTranslation()
  const { page, setPage } = useRejectedMessagesView()

  const uuidColumnValue = (row: RejectedMessage) => (
    <Typography variant="p3" color="text.main">{row.messageId}</Typography>
  )

  const dateColumnValue = (row: RejectedMessage) =>
    localDate(row.timestamp, 'full') ?? ''

  const errorsColumnValue = (row: RejectedMessage) => (
    <Typography variant="p3" color="text.main" sx={{ overflowWrap: 'break-word' }}>{row.error}</Typography>
  )

  const columns = useMemo((): KNDataTableColumn<RejectedMessage>[] => [
    {
      name: 'uuid',
      label: t('screens.cs.message_manager.table.uuid'),
      getValue: uuidColumnValue,
      sx: { width: '24rem' },
    },
    {
      name: 'date',
      label: t('screens.cs.message_manager.table.date'),
      getValue: dateColumnValue,
      sx: { width: '10rem' },
    },
    {
      name: 'errors',
      label: t('screens.cs.message_manager.table.errors'),
      getValue: errorsColumnValue,
      sx: { width: { xs: '24rem', xxl: 'auto' } },
    },
  ], [])

  return (
    <KNDataTable
      columns={columns}
      data={rejectedMessages}
      page={page}
      pageCount={pageCount}
      onPageChange={setPage}
      sx={{
        marginX: -2,
      }}
    />
  )
}

export default RejectedMessagesTable
